import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Loader2, TrendingUp } from 'lucide-react'
import { useUser } from '@/shared/hooks/useUser'
import { useResources } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'
import ResourceCard from './components/ResourceCard'
import Button from '@/components/ui/Button'
import PullToRefresh from '@/components/ui/PullToRefresh'

export default function TrafficList() {
  const navigate = useNavigate()
  const { hapticFeedback, hapticNotification, telegramId } = useTelegram()
  const { data: user, isLoading: userLoading, error: userError } = useUser(telegramId)
  const { data: resources, isLoading: resourcesLoading, error: resourcesError, refetch } = useResources(user?.id)
  const [filterType, setFilterType] = useState<'all' | 'channel' | 'bot' | 'boost'>('all')
  const [showUserLoader, setShowUserLoader] = useState(false)
  const [showResourcesLoader, setShowResourcesLoader] = useState(false)

  // Delay showing user loader to prevent flash
  useEffect(() => {
    if (userLoading) {
      const timer = setTimeout(() => setShowUserLoader(true), 200)
      return () => clearTimeout(timer)
    } else {
      setShowUserLoader(false)
    }
  }, [userLoading])

  // Delay showing resources loader to prevent flash
  useEffect(() => {
    if (resourcesLoading) {
      const timer = setTimeout(() => setShowResourcesLoader(true), 200)
      return () => clearTimeout(timer)
    } else {
      setShowResourcesLoader(false)
    }
  }, [resourcesLoading])

  const handleResourceClick = (resourceId: string) => {
    hapticFeedback('light')
    navigate(`/traffic/${resourceId}`)
  }

  const handleAddResource = () => {
    hapticFeedback('light')
    navigate('/traffic/add')
  }

  const handleRefresh = async () => {
    try {
      await refetch()
      hapticNotification?.('success')
    } catch (error) {
      console.error('Failed to refresh resources:', error)
      hapticNotification?.('error')
    }
  }

  // Show loading if user is loading and loader should be visible
  if ((userLoading || (!user && !userError)) && showUserLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  // Return null during initial load before loader timeout
  if (userLoading || (!user && !userError)) {
    return null
  }

  if (userError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ошибка загрузки пользователя</h2>
          <p className="text-sm text-tg-hint">{userError.message}</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">👤</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Пользователь не найден</h2>
          <p className="text-sm text-tg-hint">Пожалуйста, запустите бота в Telegram</p>
        </div>
      </div>
    )
  }

  // Only show resources loading state after user is loaded
  if (resourcesLoading && showResourcesLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  if (resourcesLoading) {
    return null
  }

  if (resourcesError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ошибка загрузки ресурсов</h2>
          <p className="text-sm text-tg-hint">{resourcesError.message}</p>
        </div>
      </div>
    )
  }

  // Filter resources by type
  const filteredResources = resources?.filter((resource) => {
    if (filterType === 'all') return true
    return resource.resource_type === filterType
  }) || []

  return (
    <div className="min-h-screen bg-tg-bg">
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="p-4 pb-24 pt-[96px]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-tg-text">Мой трафик</h1>
                <p className="text-sm text-tg-hint mt-1">Управляйте вашими ресурсами</p>
              </div>
              <button
                onClick={handleAddResource}
                className="flex items-center justify-center w-12 h-12 bg-tg-button hover:opacity-90 text-tg-button-text rounded-xl shadow-lg shadow-tg-button/25 active:scale-95 transition-all"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>

            {/* Type filter */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {['all', 'channel', 'bot', 'boost'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    hapticFeedback('light')
                    setFilterType(type as typeof filterType)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    filterType === type
                      ? 'bg-tg-button text-white shadow-md'
                      : 'bg-tg-secondary-bg text-tg-text hover:bg-tg-secondary-bg/80'
                  }`}
                >
                  {type === 'all' && 'Все'}
                  {type === 'channel' && 'Каналы'}
                  {type === 'bot' && 'Боты'}
                  {type === 'boost' && 'Бусты'}
                </button>
              ))}
            </div>

            {!resources || resources.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">
                  <TrendingUp className="w-16 h-16 mx-auto text-tg-hint" />
                </div>
                <h2 className="text-xl font-semibold text-tg-text mb-2">Нет ресурсов</h2>
                <p className="text-tg-hint mb-6">Добавьте свой первый ресурс для продажи трафика</p>
                <Button onClick={handleAddResource}>
                  <Plus className="w-5 h-5" />
                  Добавить ресурс
                </Button>
              </div>
            ) : filteredResources.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔍</div>
                <h2 className="text-xl font-semibold text-tg-text mb-2">Ничего не найдено</h2>
                <p className="text-tg-hint mb-6">
                  {filterType === 'channel' && 'У вас нет каналов'}
                  {filterType === 'bot' && 'У вас нет ботов'}
                  {filterType === 'boost' && 'У вас нет бустов'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredResources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    onClick={() => handleResourceClick(resource.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </PullToRefresh>
    </div>
  )
}
